import {
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  DialogPositioningBehavior,
  DialogType,
  InputLabelWidget,
  ListItem
} from '@tableau/widgets';
import {FormikProps, withFormik} from 'formik';
import React from 'react';

import {Messages} from '../../codegen/Localize';
import {imgUrl} from '../../common/utilities/ImgUrl';
import {openReactConfirmationDialog} from '../../commonInfra/dialogs/ReactConfirmationDialog';
import {ModalInstance, ReactModalService} from '../../commonInfra/dialogs/ReactModalService';
import {TabDialogWidth} from '../../commonInfra/dialogs/style/TabDialogWidth';
import {IResult} from '../../httpClient/serverApi/types/IResponse';
import {FeatureFlags} from '../../ts/FeatureFlags';
import {IEditableServerSettings} from '../../ts/ServerApi/types/IEditableServerSettings';
import {
  AnalyticsExtensionsSettingsCtrlProps,
  IAnalyticsExtensionsSettings
} from '../controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import {ServerSettingsInstance} from '../services/ServerSettings';
import {AnalyticsExtensionsSettingsStyles} from '../styles/AnalyticsExtensionsSettingsStyles';
import {
  IAnalyticsExtConnectionId,
  IAnalyticsExtConnectionSettings,
  IAnalyticsExtSiteSettings
} from '../types/ISiteSettingsForSiteAdmin';
import {AnalyticsExtensionsSettingsDialog} from './AnalyticsExtensionsSettingsDialog';

export interface AnalyticsExtensionsSettingsForSiteValues {
  showAnalyticsSiteSetting: boolean;
  showSiteCheckbox: boolean;
  enableExternalServicesCheckbox: boolean;
  connectionLUID: string;
  connectionName: string;
  connectionType: string;
  hostName: string;
  port?: number;
  isAuth?: boolean;
  userName?: string;
  password?: string;
  isSecure?: boolean;
  namedConnectionList?: IAnalyticsExtConnectionSettings[];
}

export const ConnectionTypes = {
  TABPY: {
    TAG: 'TABPY',
    DISPLAY: 'TabPy/Analytics Extension'
  },
  RSERVE: {
    TAG: 'RSERVE',
    DISPLAY: 'Rserve'
  },
  EINSTEIN: {
    TAG: 'EINSTEIN_DISCOVERY',
    DISPLAY: 'Einstein Discovery'
  },
  GENERIC_API: {
    TAG: 'GENERIC_API',
    DISPLAY: 'Analytics Extension API'
  },
  SELECT: 'Select'
};


const connectionItems: ListItem[] = [
  {value: ConnectionTypes.SELECT},
  {value: ConnectionTypes.TABPY.DISPLAY},
  {value: ConnectionTypes.RSERVE.DISPLAY}
];

const MAX_HOSTNAME_LENGTH: number = 255;
const MIN_PORT_NUMBER: number = 0;
const MAX_PORT_NUMBER: number = 65535;
const MAX_USERNAME_LENGTH: number = 255;
const MAX_PASSWORD_LENGTH: number = 255;

const EINSTEIN_DISCOVERY_CELL_ICON = imgUrl('einstein_discovery_new_connection.svg');
const TABPY_CELL_ICON = imgUrl('tabpy_new_connection.svg');
const RSERVE_CELL_ICON = imgUrl('rserve_new_connection.svg');
const ANALYTICS_EXTENSION_API_CELL_ICON = imgUrl('analytics_extensions_api.svg');
const NO_CONNECTION_ICON = imgUrl('extension.svg');

export type InnerAnalyticsExtensionsSettingForSiteProps =
  AnalyticsExtensionsSettingsCtrlProps
  & FormikProps<AnalyticsExtensionsSettingsForSiteValues>
  & AnalyticsExtensionsSettingsForSiteValues;

export function AnExtSiteSettingRemoveActionStringBody(): JSX.Element {
  return (
    <div>
      <h3 className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeader}>
        {Messages.analyticsExtensionsRemoveConnectionHeader()}
      </h3>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogDescription}>
        {Messages.analyticsExtensionsRemoveConnectionText()}
      </div>
    </div>
  );
}

export function AnExtSiteSettingRemoveActionStringBodyEDFFOn(): JSX.Element {
  return (
    <div>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeaderContainerEDFFOn}>
        <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeaderEDFFOn}>
          {Messages.analyticsExtensionsRemoveConnectionHeaderEDFFOn({CONNECTION_TYPE: 'Connection'})}
        </div>
      </div>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogDescriptionEDFFOn}
           style={{fontSize: '16px'}}>
        {Messages.analyticsExtensionsRemoveConnectionTextHeader()}
      </div>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogDescriptionEDFFOn}
           style={{fontSize: '12px'}}>
        {Messages.analyticsExtensionsRemoveConnectionTextEDFFOn()}
      </div>
    </div>
  );
}

export function confirmDeleteConnectionDialog(luid: string, request: (connectionId: IAnalyticsExtConnectionId) => Promise<IResult>) {
  const dialogInstance = openReactConfirmationDialog<void, IResult>({
    bodyComponent: AnExtSiteSettingRemoveActionStringBodyEDFFOn,
    message: '',
    paramsForAction: undefined,
    actionName: Messages.analyticsExtensionsRemoveConnectionActionNameEDFFOn(),
    action: () => request({connection_luid: luid}),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });
  return dialogInstance.result;
}

export class InnerAnalyticsExtensionsSettingForSite extends React.PureComponent <InnerAnalyticsExtensionsSettingForSiteProps> {
  private serverSettings: IEditableServerSettings;

  constructor(props: InnerAnalyticsExtensionsSettingForSiteProps) {
    super(props);
    props.setSaveCallback(() => this.sendUpdateSave());
    props.setCancelCallback(() => {
      this.handleChangeAdapter(this.props.initialValues.enableExternalServicesCheckbox, 'enableExternalServicesCheckbox');
      this.props.setIsFormDirty(false);
    });

    props.setIsBusyCallback(() => props.isSubmitting);
    this.sendDelete = this.sendDelete.bind(this);
    this.getServerSettings();
  }

  public sendUpdateSave(): Promise<IResult> {
    // need showAnalyticsSiteSetting check because current design require the hack to handle signle/multisite
    // this is how extension team does it, refactoring requires lots of work and would need to refactor their page
    if (this.props.values.showAnalyticsSiteSetting) {
      const updateSiteSettings: IAnalyticsExtSiteSettings = {enabled: this.props.values.enableExternalServicesCheckbox};
      this.props.handleSubmit();
      return this.props.sendUpdateRequest(updateSiteSettings, null);
    }
    this.props.handleSubmit();
  }

  public componentDidUpdate() {
    this.props.setIsFormDirty(this.isDirty());
    this.props.setIsFormValid(this.props.isValid);
    this.props.setIsBusyCallback(() => this.props.isSubmitting);
  }

  private handleChangeAdaptorMultipleConnections = (updatedConnection: IAnalyticsExtConnectionSettings) => {
    let connectionList = Object.assign([], this.props.values.namedConnectionList);
    let found = false;
    connectionList.forEach((connection: IAnalyticsExtConnectionSettings, index: number) => {
      if (connection.connection_luid === updatedConnection.connection_luid || connection.connection_luid === '') {
        found = true;
        connectionList[index] = updatedConnection;
      }
    });
    if (!found) {
      connectionList.push(updatedConnection);
    }
    this.handleChangeAdapter(connectionList, 'namedConnectionList');
  }

  private handleChangeAdapter = (value: any, fieldName: keyof AnalyticsExtensionsSettingsForSiteValues) => {
    const event = {
      target: {
        name: fieldName,
        value
      }
    };
    // Call the Formik handleChange
    this.props.handleChange(event);
  }

  private clearLoginInfo() {
    this.handleChangeAdapter('', 'userName');
    this.handleChangeAdapter('', 'password');
  }

  private showConnectionDialog(ctrlProps: AnalyticsExtensionsSettingsCtrlProps, connectionLUID: string) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectionLUID,
      ctrlProps: ctrlProps
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialog, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      //dialogStyle: serverAndSiteUsersDialogStyle,
      shouldNotSetDefaultWidth: true
    });
    return modalInstance.result;
  }

  private showHybridDialog(ctrlProps: AnalyticsExtensionsSettingsCtrlProps, connectionLUID: string) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectionLUID,
      ctrlProps: ctrlProps,
      // if serverSettings is undefined, we are in online which means savedAccessTokensEnabled should be true
      savedAccessTokensEnabled: this.serverSettings ? this.serverSettings.savedAccessTokensEnabled : true
    };

    modalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: this.isCreating(connectionLUID) ? Messages.analyticsExtensionsSettingsCreateConnectionDialogHeader() : Messages.analyticsExtensionsSettingsEditConnectionDialogHeader(),
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialog, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      //dialogStyle: serverAndSiteUsersDialogStyle,
      shouldNotSetDefaultWidth: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      shouldNotCloseOnGlassClick: true
    });
    return modalInstance.result;
  }

  private isCreating(connectionLUID: string) {
    if (!FeatureFlags.analyticsExtensionsNamedConnections) {
      const params = this.props.values;
      return params === undefined || params === null || params.connectionType === ConnectionTypes.SELECT;
    } else {
      return connectionLUID === '';
    }
  }

  private updateConnectionInfo(result: IAnalyticsExtConnectionSettings) {
    if (result) {
      const updateConnection = result;
      let connectionDisplay = '';
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
        connectionDisplay = ConnectionTypes.TABPY.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
        connectionDisplay = ConnectionTypes.RSERVE.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
        connectionDisplay = ConnectionTypes.EINSTEIN.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
        connectionDisplay = ConnectionTypes.GENERIC_API.DISPLAY;
      }
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        this.handleChangeAdapter(connectionDisplay, 'connectionType');
        this.handleChangeAdapter(updateConnection.connection_luid, 'connectionLUID');
        this.handleChangeAdapter(updateConnection.host, 'hostName');
      } else {
        updateConnection.connection_brief.connection_type = connectionDisplay;
        this.handleChangeAdaptorMultipleConnections(updateConnection);
      }
      this.props.setIsFormDirty(this.isDirty());
    }
  }

  private sendDelete(connectionLUID: string) {
    let result = confirmDeleteConnectionDialog(connectionLUID, this.props.sendDeleteRequest);

    // After I increased the type strictness on ReactModalService, the following result type gave an error.
    // The typedefs say that `sendDeleteRequest` returns an `IResult`, but it looks like the API
    // might actually return `IAnalyticsExtensionsSettings[]`. Since the error is many levels deep
    // and I don't own this file, I'm adding this type assertion and leaving this comment. (abrandon 2020-08-11)
    let resultOfQuestionableType = result as unknown as PromiseLike<IAnalyticsExtensionsSettings[]>;
    resultOfQuestionableType.then((result: IAnalyticsExtensionsSettings[]) => {
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        if (!result.length) {
          this.props.initialAnalyticsExtensionsSettings.connectionDefault = null;
          this.handleChangeAdapter(ConnectionTypes.SELECT, 'connectionType');
        }
      } else {
        //If named connections ff is on, we need to remove this connection from our named connections
        let initialConnectionList = this.props.initialAnalyticsExtensionsSettings.connectionSettingsList;
        initialConnectionList.forEach((connection, index) => {
          if (connection.connection_luid === connectionLUID) {
            this.props.initialAnalyticsExtensionsSettings.connectionSettingsList.splice(index, 1);
          }
        });
        let namedConnections = Object.assign([], this.props.values.namedConnectionList);
        namedConnections.forEach((connection, index) => {
          if (connection.connection_luid === connectionLUID) {
            namedConnections.splice(index, 1);
          }
        });
        if (namedConnections.length === 0) {
          let brief = defaultConnection.connection_brief;
          brief = {
            ...brief,
            connection_type: ConnectionTypes.SELECT
          };
          namedConnections.push({
            ...defaultConnection,
            connection_brief: brief
          });
        }
        this.handleChangeAdapter(namedConnections, 'namedConnectionList');
      }
      this.props.setIsFormDirty(this.isDirty());
    });
  }

  private isDirty() {
    return this.props.values.enableExternalServicesCheckbox !== this.props.initialValues.enableExternalServicesCheckbox;
  }

  private getConnectionTypeIcon(type: String) {
    switch (type) {
      case ConnectionTypes.RSERVE.DISPLAY: {
        return RSERVE_CELL_ICON;
      }
      case ConnectionTypes.TABPY.DISPLAY: {
        return TABPY_CELL_ICON;
      }
      case ConnectionTypes.EINSTEIN.DISPLAY: {
        return EINSTEIN_DISCOVERY_CELL_ICON;
      }
      case ConnectionTypes.GENERIC_API.DISPLAY: {
        return ANALYTICS_EXTENSION_API_CELL_ICON;
      }
      default: {
        return NO_CONNECTION_ICON;
      }
    }
  }

  private getServerSettings() {
    ServerSettingsInstance.fetch().then((settings) => {
      this.serverSettings = settings;
    }).catch(() => {
      this.serverSettings = { savedAccessTokensEnabled: false };
    });
  }

  render() {
    if (!this.props.values.showAnalyticsSiteSetting) {
      return (
        <section/>
      );
    }

    let extensionsDisplay;
    let extensionRows = [];

    for (let namedConnection of this.props.values.namedConnectionList) {
      extensionRows.push(
        <div
          className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableNCFFOn}>
          <div
            className={namedConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.noConnectionIcon}
                 style={{backgroundSize: 'contain', backgroundRepeat: 'no-repeat'}}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.noConnectionTextLabel} style={{textAlign: 'center'}}>
              <InputLabelWidget testId="anext_no_connection_label_nc_on"
                                className={AnalyticsExtensionsSettingsStyles.noConnectionText}
                                style={{fontSize: 18}}>
                {Messages.analyticsExtensionsNoConnectionConfiguredEDFFOn()}
              </InputLabelWidget>
            </div>
          </div>
          <div
            className={namedConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellIconNCFFOn}
                 style={{width: '10%', backgroundImage: this.getConnectionTypeIcon(namedConnection.connection_brief.connection_type)}}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_connection_name_value_nc_on">
                {namedConnection.connection_brief.connection_name}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_connection_type_value_nc_on" style={{color: '#1A699E'}}>
                {namedConnection.connection_brief.connection_type}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '5%'}}>
              <ButtonWidget
                testId={`anext_connection_delete_button_nc_on + ${namedConnection.connection_luid}`}
                buttonType={ButtonType.Icon}
                id={`editConnectionButton + ${namedConnection.connection_luid}`}
                className={AnalyticsExtensionsSettingsStyles.editConnectionButton}
                handleClick={() => {
                  this.showHybridDialog(this.props, namedConnection.connection_luid).then(
                    (result: IAnalyticsExtConnectionSettings) => {
                      this.updateConnectionInfo(result);
                    }
                  );
                }}>
              </ButtonWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn}
                 style={{width: '5%', verticalAlign: 'middle'}}>
              <ButtonWidget
                testId={`anext_connection_delete_button_nc_on + ${namedConnection.connection_luid}`}
                buttonType={ButtonType.Icon}
                id={`deleteConnectionButton + ${namedConnection.connection_luid}`}
                className={AnalyticsExtensionsSettingsStyles.deleteConnectionButton}
                onClick={() => this.sendDelete(namedConnection.connection_luid)}>
              </ButtonWidget>
            </div>
          </div>
        </div>
      );
    }
    extensionsDisplay =
      <div>
        <div
          className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableNCFFOn}>
          <div className={AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} style={{width: '10%'}}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn}
                 style={{width: '40%', verticalAlign: 'middle'}}>
              <InputLabelWidget testId="anext_connection_name_title_nc_on" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsConnectionName()}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn}
                 style={{width: '50%', verticalAlign: 'middle'}}>
              <InputLabelWidget testId="anext_connection_type_title_nc_on" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsConnectionType()}
              </InputLabelWidget>
            </div>
          </div>
        </div>
        {extensionRows}
      </div>;

    return (
      <section>
        <div
          className={FeatureFlags.analyticsExtensionsNamedConnections ? AnalyticsExtensionsSettingsStyles.enableForSiteNamedConnectionsOn : AnalyticsExtensionsSettingsStyles.enableForSite}>
          <CheckBoxWithLabelWidget
            checked={this.props.values.enableExternalServicesCheckbox}
            handleChange={(isBoxChecked: boolean) => {
              this.handleChangeAdapter(isBoxChecked, 'enableExternalServicesCheckbox');
            }}
            testId="anext_site_enable_checkbox"
            label={Messages.analyticsExtensionsSettingsSite_checkbox()}
            disabled={!this.props.values.showSiteCheckbox}
          />
          <div
            className={(FeatureFlags.analyticsExtensionsNamedConnections) ? null : AnalyticsExtensionsSettingsStyles.Hide}>
            <ButtonWidget
              testId="anext_create_new_connection_button"
              buttonType={ButtonType.Primary}
              id="addNewConnectionBtn"
              handleClick={() => {
                this.showHybridDialog(this.props, '').then(
                  (result: IAnalyticsExtConnectionSettings) => {
                    this.updateConnectionInfo(result);
                  }
                );
              }}>{Messages.analyticsExtensionsSettingsCreateConnection()}</ButtonWidget>
          </div>
        </div>
        <div>
          {extensionsDisplay}
        </div>
      </section>
    );
  }
}

export let defaultConnection: IAnalyticsExtConnectionSettings = {
  connection_brief: {
    connection_type: 'SELECT',
    connection_name: ''
  },
  connection_luid: '',
  host: '',
  port: undefined,
  is_ssl_enabled: false,
  is_auth_enabled: false,
  username: '',
  password: ''
};

function getConnectionTypeDisplay(initInfoConnection: IAnalyticsExtConnectionSettings) {
  let connection = ConnectionTypes.SELECT;
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
    connection = ConnectionTypes.TABPY.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
    connection = ConnectionTypes.RSERVE.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
    connection = ConnectionTypes.EINSTEIN.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
    connection = ConnectionTypes.GENERIC_API.DISPLAY;
  }
  return connection;
}

export const AnalyticsExtensionsSettingsForSite =
  withFormik<AnalyticsExtensionsSettingsCtrlProps, AnalyticsExtensionsSettingsForSiteValues>({
    enableReinitialize: true,   // Reset the form when initial props change
    validateOnBlur: true,
    mapPropsToValues: props => {
      let initInfoSite = false;
      let initInfoConnection = defaultConnection;
      let connectionType = ConnectionTypes.SELECT;
      let namedConnections: IAnalyticsExtConnectionSettings[] = [];
      if (typeof props.initialAnalyticsExtensionsSettings !== 'undefined') {
        initInfoSite = props.initialAnalyticsExtensionsSettings.enableExtSvcSite;
      }

      // If named connections feature flag is off, we use connectionDefault
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        if (props.initialAnalyticsExtensionsSettings?.connectionDefault) {
          initInfoConnection = props.initialAnalyticsExtensionsSettings.connectionDefault;
        }
        connectionType = getConnectionTypeDisplay(initInfoConnection);
      } else if (FeatureFlags.analyticsExtensionsNamedConnections) {
        //If named connections feature flag is on, we map connection list to a set of connection configurations
        if (!props.initialAnalyticsExtensionsSettings || props.initialAnalyticsExtensionsSettings?.connectionSettingsList.length === 0) {
          let connectionBrief = {... defaultConnection.connection_brief, connection_type: connectionType};
          namedConnections.push({...defaultConnection, connection_brief: connectionBrief});
        } else {
          for (let connection of props.initialAnalyticsExtensionsSettings?.connectionSettingsList) {
            connectionType = getConnectionTypeDisplay(connection);
            namedConnections.push({
              connection_brief: {
                connection_name: connection.connection_brief.connection_name,
                connection_type: connectionType,
              },
              connection_luid: connection.connection_luid,
              host: connection.host,
              port: connection.port,
              is_ssl_enabled: connection.is_ssl_enabled,
              is_auth_enabled: connection.is_auth_enabled,
              username: connection.username,
              password: connection.password,
            });
          }
        }
      }

      return {
        showAnalyticsSiteSetting: props.displaySettings,
        showSiteCheckbox: props.displaySiteSettingCheckbox,
        enableExternalServicesCheckbox: initInfoSite,
        connectionLUID: initInfoConnection.connection_luid,
        connectionName: initInfoConnection.connection_brief.connection_name,
        connectionType: connectionType,
        hostName: initInfoConnection.host,
        port: initInfoConnection.port,
        isSecure: initInfoConnection.is_ssl_enabled,
        isAuth: initInfoConnection.is_auth_enabled,
        userName: initInfoConnection.username,
        password: initInfoConnection.password,
        namedConnectionList: namedConnections
      };
    },
    validate: (values, props) => {
      const errors: any = {};
      const invalid = 'Invalid';
      return errors;
    },
    handleSubmit: (values, actions) => {
      // Reset the form after submission happens
      actions.setSubmitting(false);
      actions.resetForm({values});
    },
  })(InnerAnalyticsExtensionsSettingForSite);
